import API from "./axios";

// ── Department ────────────────────────────────────────────────────────────────
export const departmentApi = {
  getAll:    ()       => API.get("/departments"),
  getActive: ()       => API.get("/departments/active"),
  getById:   (id)     => API.get(`/departments/${id}`),
  create:    (data)   => API.post("/departments", data),
  update:    (id, d)  => API.put(`/departments/${id}`, d),
  toggle:    (id)     => API.patch(`/departments/${id}/toggle`),
  delete:    (id)     => API.delete(`/departments/${id}`),

  // Narrow endpoint. PUT /departments/{id} refuses the Cutting department —
  // it is structural — but Cutting is the first stage a customer follows, so
  // it still has to be tickable as an app tracking stage.
  setPortalStage: (id, show, sortOrder) => {
    const p = new URLSearchParams();
    if (show != null) p.append("show", show);
    if (sortOrder != null) p.append("sortOrder", sortOrder);
    return API.patch(`/departments/${id}/portal-stage?${p.toString()}`);
  },
};

// ── Cutting Issue ─────────────────────────────────────────────────────────────
export const cuttingIssueApi = {
  // locationId (optional) — only that location's issues (Cutting Inward's picker).
  getAll:           (locationId) => API.get("/cutting-issues", { params: locationId ? { locationId } : {} }),
  // Phase 5 — server-side pagination + sort + search.
  // params: { page, size, sortBy, sortDir, q, status, departmentId, employeeId, locationId }
  paged:            (params = {}) => API.get("/cutting-issues/paged", { params }),
  getById:          (id)     => API.get(`/cutting-issues/${id}`),
  create:           (data)   => API.post("/cutting-issues", data),
  update:           (id, d)  => API.put(`/cutting-issues/${id}`, d),
  markIssued:       (id)     => API.patch(`/cutting-issues/${id}/issue`),
  cancelIssue:      (id)     => API.patch(`/cutting-issues/${id}/cancel`),
  // Close a PARTIAL_RETURNED issue short — the uncut balance goes back to the order.
  closeIssue:       (id)     => API.patch(`/cutting-issues/${id}/close`),
  // Estimated vs actual (cloth m/pc and cost, size-wise) — CUTTING_ANALYSIS.
  analysis:         (id)     => API.get(`/cutting-issues/${id}/analysis`),
  analysisPdf:      (id)     => API.get(`/cutting-issues/${id}/analysis/pdf`, { responseType: "blob" }),
  analysisExcel:    (id)     => API.get(`/cutting-issues/${id}/analysis/excel`, { responseType: "blob" }),
  // Phase 36da.99.ap — delete a DRAFT cutting issue.  Backend rejects with
  // 400 if the issue is anything past DRAFT.  No stock revert needed since
  // DRAFT never deducted stock; the row + child rows are purged via JPA cascade.
  deleteDraft:      (id)     => API.delete(`/cutting-issues/${id}`),
  calculateMeters:  (productId, soItemId, qty) =>
    API.get(`/cutting-issues/calculate-meters?productId=${productId}&soItemId=${soItemId}&qty=${qty}`),
  // Phase 36da.99.cmrecalc — recalc from the typed per-size ISSUE qty
  // (sizeQtys = { sizeValue: qty }), so meters follow the issue boxes live.
  calculateMetersForIssue: (productId, soItemId, sizeQtys) =>
    API.post(`/cutting-issues/calculate-meters?productId=${productId}&soItemId=${soItemId}`, sizeQtys || {}),
  // locationId (optional) — remaining = that location's share of the SO minus what it cut.
  // job: what a job may still give the factory `locationId` (its rest plus the main location's).
  getSoRemaining:   (locationId, job) => API.get("/cutting-issues/so-remaining", {
    params: { ...(locationId ? { locationId } : {}), ...(job ? { job: true } : {}) },
  }),
};

// ── Cutting Inward ────────────────────────────────────────────────────────────
export const cuttingInwardApi = {
  getAll:      (locationId) => API.get("/cutting-inwards", { params: locationId ? { locationId } : {} }),
  // Phase 5 — server-side pagination + sort + search.
  // params: { page, size, sortBy, sortDir, q, status, employeeId, locationId }
  paged:       (params = {}) => API.get("/cutting-inwards/paged", { params }),
  getById:     (id)      => API.get(`/cutting-inwards/${id}`),
  getByIssue:  (issueId) => API.get(`/cutting-inwards/by-issue/${issueId}`),
  create:      (data)    => API.post("/cutting-inwards", data),
  update:      (id, d)   => API.put(`/cutting-inwards/${id}`, d),
  // Only the cloth returned — also on a CONFIRMED inward whose job work is made.
  updateClothReturns: (id, returns) => API.put(`/cutting-inwards/${id}/cloth-returns`, returns),
  cancel:      (id)      => API.patch(`/cutting-inwards/${id}/cancel`),
  confirm:     (id)      => API.patch(`/cutting-inwards/${id}/confirm`),
  // Phase 36da.99.dd — delete a DRAFT cutting inward.
  deleteDraft: (id)      => API.delete(`/cutting-inwards/${id}`),
};

// ── Dept Issue ────────────────────────────────────────────────────────────────
export const deptIssueApi = {
  // locationId (optional) — only that location's issues (Dept Inward's picker).
  getAll:         (locationId) => API.get("/production/dept-issues", { params: locationId ? { locationId } : {} }),
  // Phase 5 — server-side pagination + sort + search.
  // params: { page, size, sortBy, sortDir, q, status, departmentId, employeeId, locationId }
  paged:          (params = {}) => API.get("/production/dept-issues/paged", { params }),
  getById:        (id)   => API.get(`/production/dept-issues/${id}`),
  create:         (data) => API.post("/production/dept-issues", data),
  update:         (id, data) => API.put(`/production/dept-issues/${id}`, data),
  markIssued:     (id)   => API.patch(`/production/dept-issues/${id}/issue`),
  cancelIssue:    (id)   => API.patch(`/production/dept-issues/${id}/cancel`),
  // Phase 36da.99.dd — delete a DRAFT dept issue.
  deleteDraft:    (id)   => API.delete(`/production/dept-issues/${id}`),
  getSoRemaining: (deptId, locationId) => API.get("/production/dept-issues/so-remaining", {
    params: { ...(deptId ? { deptId } : {}), ...(locationId ? { locationId } : {}) },
  }),
  getProductRate: (productId, deptId) => API.get(`/production/dept-issues/product-rate?productId=${productId}&deptId=${deptId}`),
  pendingInward:  (employeeId) => API.get(`/production/dept-issues/pending-inward?employeeId=${employeeId}`),
};

/**
 * What material a set of garments needs, size-wise.
 *
 * Shared by dept issue and contractor issue — one calculator, so the two can
 * never disagree about what a garment needs. Takes a LIST because an issue
 * routinely covers several sale-order lines and the store wants one
 * consolidated pick list; rows merge on material + material size.
 *
 *   demands: [{ productId, qtyByItemSizeId: { <itemSizeId>: qty } }]
 */
export const materialRequirementApi = {
  calculate: (demands) => API.post("/production/material-requirements", demands),
  /**
   * Brands of one material, for the optional variant picker on an issue line.
   * Fetched on demand — most lines never need one, so loading every material's
   * brands up front would be wasted work.
   */
  variantsByMaterial: (materialId) => API.get(`/material-variant/by-material/${materialId}`),
  /**
   * On-hand stock for one material, narrowed the way an issue is made:
   * chain + whole-material units + a variant -> variant-size tree, each node
   * carrying its own availability. Availability matches what the FIFO will
   * allow, so the figure on screen is the figure that can actually be taken.
   */
  // locationId (optional) — only that location's stock.
  issueStock: (materialId, locationId) =>
    API.get("/production/material-issue-stock", {
      params: { materialId, ...(locationId ? { locationId } : {}) },
    }),
};

// ── Dept Inward ───────────────────────────────────────────────────────────────
export const deptInwardApi = {
  getAll:       ()          => API.get("/production/dept-inwards"),
  // Phase 5 — server-side pagination + sort + search.
  // params: { page, size, sortBy, sortDir, q, status, departmentId, locationId }
  paged:        (params = {}) => API.get("/production/dept-inwards/paged", { params }),
  // Phase 36da.99.wf — single-row fetch for the dashboard detail modal.
  getById:      (id)         => API.get(`/production/dept-inwards/${id}`),
  create:       (data)      => API.post("/production/dept-inwards", data),
  // Phase 36da.99.ed — EDIT a dept inward (DRAFT or CONFIRMED).  Editing a
  // CONFIRMED inward reverts its stock effects (ready stock deleted, bundles
  // un-consumed, issue back to ISSUED), rebuilds the lines, then re-confirms.
  // Backend blocks it if a job work entry exists against the inward.
  update:       (id, data)  => API.put(`/production/dept-inwards/${id}`, data),
  confirm:      (id, data)  => API.patch(`/production/dept-inwards/${id}/confirm`, data),
  cancelInward: (id)        => API.patch(`/production/dept-inwards/${id}/cancel`),
  // Phase 36da.99.dd — delete a DRAFT dept inward.
  deleteDraft:  (id)        => API.delete(`/production/dept-inwards/${id}`),
};

// ── Ready Stock ───────────────────────────────────────────────────────────────
export const readyStockApi = {
  getAll:   ()      => API.get("/production/ready-stock"),
  getBySO:  (soId)  => API.get(`/production/ready-stock/so/${soId}`),
};

// ── Employee ──────────────────────────────────────────────────────────────────
export const employeeApi = {
  getAll:      ()        => API.get("/employees"),
  /** Phase 5 — paged register. params: { page, size, sortBy, sortDir, q, isActive, departmentId } */
  paged:       (params)  => API.get("/employees/paged", { params }),
  // locationId (optional) — employees at that location + those with no location.
  getActive:   (locationId) => API.get("/employees/active", { params: locationId ? { locationId } : {} }),
  /** Phase 36da.99.tl — active team leaders for the employee-master dropdown */
  teamLeaders: ()        => API.get("/employees/team-leaders"),
  /** Punching ids in use, each with the employee behind it — for the register
   *  dropdown. Employee comes back with the id because a bare list of numbers
   *  is unreadable. */
  punchingIds: ()        => API.get("/employees/punching-ids"),
  /** Employee register exports — blob download, one employee per row. */
  exportExcel: (params = {}) => API.get("/employees/export/excel", { params, responseType: "blob" }),
  exportPdf:   (params = {}) => API.get("/employees/export/pdf", { params, responseType: "blob" }),
  getByDept:   (deptId, locationId) =>
    API.get(`/employees/by-dept/${deptId}`, { params: locationId ? { locationId } : {} }),
  getById:     (id)      => API.get(`/employees/${id}`),
  create:      (data)    => API.post("/employees", data),
  update:      (id, d)   => API.put(`/employees/${id}`, d),
  toggle:      (id)      => API.patch(`/employees/${id}/toggle`),
  delete:      (id)      => API.delete(`/employees/${id}`),
};
// ── Contractor Finished Goods ─────────────────────────────────────────────────
export const contractorFinishedGoodsApi = {
  getAll:         ()         => API.get("/contractor-finished-goods"),
  getAvailable:   ()         => API.get("/contractor-finished-goods/available"),
  moveToReadyStock: (id, qty) => API.post(`/contractor-finished-goods/${id}/move-to-ready-stock?qty=${qty}`),
};

// ── Contractor Inward Bundles (for Dept Issue source picker) ──────────────────
export const contractorInwardBundleApi = {
  getAvailableForDeptIssue: (toDeptId) => API.get("/production/dept-issues/available-contractor-bundles" + (toDeptId ? `?toDeptId=${toDeptId}` : "")),
  getDeptInwardForDeptIssue: (toDeptId) => API.get("/production/dept-issues/available-dept-inward-bundles" + (toDeptId ? `?toDeptId=${toDeptId}` : "")),
};

export const contractorIssueBundleApi = {
  getContractorInwardBundles: (contractorTypeId) => API.get("/contractor-issues/available-contractor-inward-bundles" + (contractorTypeId ? `?contractorTypeId=${contractorTypeId}` : "")),
  getDeptInwardBundles:       (contractorTypeId) => API.get("/contractor-issues/available-dept-inward-bundles" + (contractorTypeId ? `?contractorTypeId=${contractorTypeId}` : "")),
};